import { memo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FastField, Form, Formik } from "formik";
import AUTH_SERVICES from "../../authService";
import { resolveNextStep } from "../../authFlow";
import { beginSignupSession, clearSignupSession } from "../../authSession";
import { parseAuthError, preferServerReason } from "../../authErrors";
import { CONTACT_SCHEMA } from "../../validationSchemas";
import { ContactField, SubmitButton } from "../../components/fields";
import { COUNTRY_OPTIONS, detectCountry } from "../../countryOptions";
import AuthFooter, { LegalLink } from "../../components/AuthFooter";
import {
  defaultOtpChannel,
  getDialCode,
  normalizeEmail,
  toE164,
} from "../../authMappers";

import "../styles.css";

const ContactSection = memo(function ContactSection() {
  return (
    <FastField name="channelType">
      {({ field }) => {
        const isEmail = field.value === "email";

        return (
          <>

            <header className="auth-head">
              <h1>Get Started</h1>
              <p className="auth-sub">
                We&rsquo;ll send you a code, it help us keep your account
                secure.
              </p>
            </header>

            <ContactField
              name="input"
              label={isEmail ? "Email address" : "Phone number"}
              isPhone={!isEmail}
              countryOptions={COUNTRY_OPTIONS}
              placeholder={isEmail ? "you@example.com" : "024 000 0167"}
            />
          </>
        );
      }}
    </FastField>
  );
});

const ChannelSwitch = memo(function ChannelSwitch({ onSwitched }) {
  return (
    <FastField name="channelType">
      {({ field, form }) => {
        const isEmail = field.value === "email";
        const next = isEmail ? "phone" : "email";
        const switchTo = () => {
          form.setValues({ ...form.values, channelType: next, input: "" });
          form.setTouched({});
          form.setErrors({});
          onSwitched();
        };

        return (
          <p className="auth-switch">
            <button type="button" className="auth-switch-btn" onClick={switchTo}>
              {isEmail ? "Use phone number instead" : "Use email instead"}
            </button>
          </p>
        );
      }}
    </FastField>
  );
});

const Footer = memo(function Footer() {
  return (
    <AuthFooter enterprise="By Sayaty Enterprise">
      By registering, you accept our{" "}
      <LegalLink to="/terms">Terms and Conditions</LegalLink> and{" "}
      <LegalLink to="/privacy">Privacy Policy</LegalLink>
    </AuthFooter>
  );
});

const GetStarted = () => {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);


  const clearError = useCallback(() => setError(""), []);
  console.log("isEmail");

  const submit = useCallback(
    async ({ channelType, country, input }) => {
      const isEmail = channelType === "email";
      const value = isEmail ? normalizeEmail(input) : toE164(input, country);
      setError("");
      setLoading(true);
      clearSignupSession();
      beginSignupSession({
        sessionId: null,
        stage: "CHANNEL_1",
        contact: value,
        channelType,
        countryCode: country,
        callingCode: getDialCode(country),
      });

      try {
        const response = await AUTH_SERVICES.sessionStart({
          channelType,
          value,
          otpChannel: defaultOtpChannel(channelType),
        });

        const decision = await resolveNextStep(response.data, { origin: "start" });

        // `replace`, not `push`: going back to a submitted Get Started and
        // resubmitting would mint a second session.
        navigate(decision.route, {
          replace: true,
          state: {
            stage: decision.stage,
            twoInputs: decision.twoInputs,
            mode: decision.mode,
            legKey: decision.legKey,
          },
        });
      } catch (caught) {
        // Server wording wins here: a deactivated account answers
        // 400 not.allowed / "Identity is not active", which classifies as
        // UNKNOWN, and the canned copy would hide the real reason.
        setError(preferServerReason(parseAuthError(caught)));
        clearSignupSession();
      } finally {
        setLoading(false);
      }
    },
    [navigate]
  );
  return (
    <div className="auth-page auth-page--alt auth-page--stacked">
      <Formik
        initialValues={{
          channelType: "phone",
          country: detectCountry(),
          input: "",
        }}
        validationSchema={CONTACT_SCHEMA}
        onSubmit={submit}
      >
        <Form className="auth-screen auth-screen--stacked" noValidate>
          <ContactSection />

          <p className="auth-error" aria-live="polite">
            {error}
          </p>

          <SubmitButton loading={loading}>Continue</SubmitButton>

          <ChannelSwitch onSwitched={clearError} />

          <Footer />
        </Form>
      </Formik>
    </div>
  );
};

export default GetStarted;
